/**
 * Tools Resource
 *
 * Thin 1:1 mapping to the tools/MCP API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  RegisterMCPRequest,
  RegisterMCPResponse,
  ListMCPsResponse,
  UpdateMCPRequest,
  UpdateMCPResponse,
  DeleteMCPResponse,
  ReconcileMCPEndpoint,
  ReconcileDeviceMcpResult,
  ToolCatalogResponse,
  ListMCPToolsResponse,
  ExecuteToolRequest,
  ExecuteToolResponse,
  ListAvailableToolsResponse,
  ListAvailableSkillsResponse,
} from '../types/index.js';

export class ToolsResource {
  constructor(private http: HttpLayer) {}

  // --- Device-scoped MCP endpoints ---

  private mcpPrefix(deviceId: string): string {
    return `/v1/devices/${deviceId}/mcp`;
  }

  async registerMCP(
    deviceId: string,
    params: RegisterMCPRequest,
  ): Promise<RegisterMCPResponse> {
    return this.http.post(this.mcpPrefix(deviceId), params);
  }

  async listMCPs(deviceId: string): Promise<ListMCPsResponse> {
    return this.http.get(this.mcpPrefix(deviceId));
  }

  async updateMCP(
    deviceId: string,
    mcpId: string,
    params: UpdateMCPRequest,
  ): Promise<UpdateMCPResponse> {
    return this.http.put(
      `${this.mcpPrefix(deviceId)}/${mcpId}`,
      params,
    );
  }

  async deleteMCP(
    deviceId: string,
    mcpId: string,
  ): Promise<DeleteMCPResponse> {
    return this.http.delete(
      `${this.mcpPrefix(deviceId)}/${mcpId}`,
    );
  }

  /**
   * Converge a device's persisted MCP registrations onto the live set its
   * daemon is actually running: adopt the reported servers, reap the rest.
   *
   * `endpoints` is the device's COMPLETE live set, and the empty array is
   * meaningful — it reaps every registration for this device. Omitting the
   * field is a 4xx, never an implicit reap-all, so a caller that failed to
   * enumerate its servers can never be read as a caller with none.
   *
   * The server owns `scope`/`location` (derived from `transport`) and pins
   * `device_id` to the path; those fields may be sent, but a value that
   * disagrees is rejected by name rather than silently corrected. `enabled` and
   * `description` are platform-owned and are never written by a reconcile, so
   * an admin-disabled server stays disabled across daemon restarts.
   *
   * Not agent-reachable: it is excluded from the `platform_call` dispatch
   * allowlist. The reconcile carries a daemon-authoritative live set with a
   * whole-device reap, and an agent has no live set to report — a dispatchable
   * reconcile could only ever destroy registrations.
   */
  async reconcileDeviceMcp(
    deviceId: string,
    endpoints: ReconcileMCPEndpoint[],
  ): Promise<ReconcileDeviceMcpResult> {
    return this.http.post(`${this.mcpPrefix(deviceId)}/reconcile`, { endpoints });
  }

  async getToolCatalog(
    deviceId: string,
  ): Promise<ToolCatalogResponse> {
    return this.http.get(`${this.mcpPrefix(deviceId)}/catalog`);
  }

  async listMCPTools(
    deviceId: string,
    mcpId: string,
  ): Promise<ListMCPToolsResponse> {
    return this.http.get(
      `${this.mcpPrefix(deviceId)}/${mcpId}/tools`,
    );
  }

  // --- Tenant-scoped tool endpoints ---

  async listTenantMCPs(): Promise<ListMCPsResponse> {
    return this.http.get('/v1/tools/mcps');
  }

  async listTenantMCPTools(
    mcpId: string,
  ): Promise<ListMCPToolsResponse> {
    return this.http.get(`/v1/tools/mcps/${mcpId}/tools`);
  }

  async getTenantToolCatalog(): Promise<ToolCatalogResponse> {
    return this.http.get('/v1/tools/catalog');
  }

  // --- Tool execution ---

  async listAvailableTools(
    workspaceId?: string | null,
    deviceId?: string,
  ): Promise<ListAvailableToolsResponse> {
    const path = workspaceId
      ? `/v1/tools/${workspaceId}/available`
      : `/v1/tools/available`;
    return this.http.get(path, { device_id: deviceId });
  }

  async executeTool(
    workspaceId: string | null | undefined,
    params: ExecuteToolRequest,
  ): Promise<ExecuteToolResponse> {
    const path = workspaceId
      ? `/v1/tools/${workspaceId}/execute`
      : `/v1/tools/execute`;
    return this.http.post(path, params);
  }

  /**
   * Discover the workspace's `.shogunchop/skills/` index. A thin read mirroring listAvailableTools; the palette merges
   * these skill-kind capabilities with listAvailableTools' coded tools.
   * `deviceId` selects the local (daemon) substrate for workspaces with no
   * cloud repository — same optional threading as listAvailableTools.
   */
  async listAvailableSkills(
    workspaceId: string,
    deviceId?: string,
  ): Promise<ListAvailableSkillsResponse> {
    return this.http.get(`/v1/tools/${workspaceId}/skills`, { device_id: deviceId });
  }
}
